/**
 * ic-compare.js — Product Compare Feature
 * Infinity Collectables | Dawn theme
 *
 * Features:
 *  - sessionStorage persistence (max 4 products)
 *  - Login gate (requires account sign-in)
 *  - Floating bar with product slots
 *  - Slide-out drawer (desktop) / full-screen modal (mobile)
 *  - Side-by-side comparison table with metafields
 *  - Highlight differences toggle
 *  - Shareable URL (?compare=handle1,handle2,...)
 *  - GA4/GTM events: compare_add, compare_remove, compare_view_opened, compare_add_to_cart
 *  - Add to cart from compare view
 *  - CLS-safe (all fixed/absolute positioned elements)
 */

(function () {
  'use strict';

  /* ─────────────────────────────────────────────────────────────
     Constants
  ───────────────────────────────────────────────────────────── */
  const MAX_PRODUCTS  = 4;
  const MIN_TO_COMPARE = 2;
  const STORAGE_KEY   = 'ic_compare_products';
  const TOAST_DURATION = 3000;

  const cfg = window.icCompareConfig || {
    customerLoggedIn: false,
    loginUrl: '/account/login',
    registerUrl: '/account/register',
    cartAddUrl: '/cart/add.js',
    currency: 'GBP',
    moneyFormat: '£{{amount}}'
  };

  /* ─────────────────────────────────────────────────────────────
     State
  ───────────────────────────────────────────────────────────── */
  let state = {
    products: [],       // Array of product data objects
    drawerOpen: false,
    toastTimer: null,
    focusTrapCleanup: null
  };

  /* ─────────────────────────────────────────────────────────────
     DOM helpers
  ───────────────────────────────────────────────────────────── */
  const $  = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));

  /* ─────────────────────────────────────────────────────────────
     Money formatting
  ───────────────────────────────────────────────────────────── */
  function formatMoney(cents) {
    if (!cents && cents !== 0) return '';
    const amount = (parseInt(cents, 10) / 100).toFixed(2);
    const fmt = (cfg.moneyFormat || '£{{amount}}')
      .replace('{{amount}}',      amount)
      .replace('{{amount_no_decimals}}', Math.floor(amount))
      .replace('{{amount_with_comma_separator}}', amount.replace('.', ','));
    return fmt;
  }

  /* ─────────────────────────────────────────────────────────────
     Session storage
  ───────────────────────────────────────────────────────────── */
  function loadFromStorage() {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch (e) {
      return [];
    }
  }

  function saveToStorage(products) {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(products));
    } catch (e) { /* quota exceeded — silently ignore */ }
  }

  /* ─────────────────────────────────────────────────────────────
     GA4 / GTM event helper
  ───────────────────────────────────────────────────────────── */
  function trackEvent(eventName, params) {
    try {
      if (typeof window.dataLayer !== 'undefined') {
        window.dataLayer.push({ event: eventName, ...params });
      }
      if (typeof window.gtag === 'function') {
        window.gtag('event', eventName, params);
      }
    } catch (e) { /* analytics must never break UX */ }
  }

  /* ─────────────────────────────────────────────────────────────
     Toast
  ───────────────────────────────────────────────────────────── */
  function showToast(message) {
    const toast = $('#ic-cmp-toast');
    if (!toast) return;
    clearTimeout(state.toastTimer);
    toast.textContent = message;
    toast.classList.add('is-visible');
    state.toastTimer = setTimeout(() => toast.classList.remove('is-visible'), TOAST_DURATION);
  }

  /* ─────────────────────────────────────────────────────────────
     URL sharing helpers
  ───────────────────────────────────────────────────────────── */
  function getShareableUrl() {
    const handles = state.products.map(p => p.handle).join(',');
    const url = new URL(window.location.href);
    if (handles) {
      url.searchParams.set('compare', handles);
    } else {
      url.searchParams.delete('compare');
    }
    return url.toString();
  }

  function readUrlParams() {
    try {
      const params = new URLSearchParams(window.location.search);
      return params.get('compare') ? params.get('compare').split(',').filter(Boolean) : [];
    } catch (e) {
      return [];
    }
  }

  /* ─────────────────────────────────────────────────────────────
     Product data extraction from DOM toggle button
  ───────────────────────────────────────────────────────────── */
  function extractProductData(btn) {
    return {
      id:           btn.dataset.productId,
      handle:       btn.dataset.productHandle,
      title:        btn.dataset.productTitle,
      url:          btn.dataset.productUrl,
      price:        btn.dataset.productPrice,
      comparePrice: btn.dataset.productComparePrice || null,
      available:    btn.dataset.productAvailable === 'true',
      image:        btn.dataset.productImage || '',
      vendor:       btn.dataset.productVendor || '',
      type:         btn.dataset.productType || '',
      tags:         (btn.dataset.productTags || '').split(',').map(t => t.trim()).filter(Boolean),
      rating:       parseFloat(btn.dataset.productRating) || null,
      ratingCount:  parseInt(btn.dataset.productRatingCount, 10) || null,
      ratingMax:    parseFloat(btn.dataset.productRatingMax) || 5,
      variantId:    btn.dataset.variantId || '',
      metafields: {
        material:    btn.dataset.productMaterial   || '',
        dimensions:  btn.dataset.productDimensions  || '',
        packSize:    btn.dataset.productPackSize    || '',
        ageRating:   btn.dataset.productAgeRating   || '',
        players:     btn.dataset.productPlayers     || '',
        playTime:    btn.dataset.productPlayTime    || '',
        licensed:    btn.dataset.productLicensed    || ''
      }
    };
  }

  /* ─────────────────────────────────────────────────────────────
     Brand / franchise from tags
  ───────────────────────────────────────────────────────────── */
  function getBrandFromTags(tags) {
    if (!tags || !tags.length) return '';
    // Look for a "brand:" or "franchise:" tag prefix first
    const branded = tags.find(t => /^(brand|franchise):/i.test(t));
    if (branded) return branded.replace(/^(brand|franchise):/i, '').trim();
    return tags[0] || '';
  }

  /* ─────────────────────────────────────────────────────────────
     Toggle a product in/out of the compare list
  ───────────────────────────────────────────────────────────── */
  function toggleProduct(btn) {
    const id = btn.dataset.productId;
    const idx = state.products.findIndex(p => p.id === id);

    if (idx > -1) {
      // Remove
      const removed = state.products.splice(idx, 1)[0];
      saveToStorage(state.products);
      syncAllToggles();
      updateBar();
      trackEvent('compare_remove', {
        product_id:    removed.id,
        product_title: removed.title,
        product_handle: removed.handle
      });
    } else {
      // Add
      if (state.products.length >= MAX_PRODUCTS) {
        showToast(`You can compare up to ${MAX_PRODUCTS} products at a time.`);
        return;
      }
      const product = extractProductData(btn);
      state.products.push(product);
      saveToStorage(state.products);
      syncAllToggles();
      updateBar();
      trackEvent('compare_add', {
        product_id:    product.id,
        product_title: product.title,
        product_handle: product.handle
      });
    }
  }

  /* ─────────────────────────────────────────────────────────────
     Sync all toggle buttons on page to current state
  ───────────────────────────────────────────────────────────── */
  function syncAllToggles() {
    const selectedIds = new Set(state.products.map(p => p.id));
    $$('[data-ic-cmp-toggle]').forEach(btn => {
      const active = selectedIds.has(btn.dataset.productId);
      btn.setAttribute('aria-pressed', String(active));
      btn.setAttribute('aria-label',
        (active ? 'Remove from compare: ' : 'Compare ') + (btn.dataset.productTitle || '')
      );
    });
  }

  /* ─────────────────────────────────────────────────────────────
     Bar rendering
  ───────────────────────────────────────────────────────────── */
  function updateBar() {
    const bar     = $('#ic-cmp-bar');
    const slots   = $('#ic-cmp-bar-slots');
    const count   = $('#ic-cmp-bar-count');
    const openBtn = $('#ic-cmp-bar-open');
    const spacer  = $('#ic-cmp-body-spacer');
    if (!bar) return;

    const n = state.products.length;

    // Show/hide bar
    bar.classList.toggle('is-visible', n > 0);
    if (spacer) spacer.classList.toggle('is-active', n > 0);

    // Count text
    if (count) {
      count.textContent = n > 0 ? `${n} of ${MAX_PRODUCTS} selected` : '';
    }

    // Enable/disable compare button
    if (openBtn) {
      openBtn.disabled = n < MIN_TO_COMPARE;
    }

    // Render slots
    if (!slots) return;
    slots.innerHTML = '';

    state.products.forEach(product => {
      const slot = document.createElement('div');
      slot.className = 'ic-cmp-bar__slot';
      slot.dataset.productId = product.id;

      const imgHtml = product.image
        ? `<img src="${product.image}" alt="${escHtml(product.title)}" class="ic-cmp-bar__slot-img" loading="lazy" width="36" height="36">`
        : `<div class="ic-cmp-bar__slot-img" aria-hidden="true"></div>`;

      slot.innerHTML = `
        ${imgHtml}
        <span class="ic-cmp-bar__slot-title">${escHtml(product.title)}</span>
        <button
          type="button"
          class="ic-cmp-bar__slot-remove"
          aria-label="Remove ${escHtml(product.title)} from comparison"
          data-remove-id="${escHtml(product.id)}"
        >
          <svg viewBox="0 0 10 10" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <line x1="1" y1="1" x2="9" y2="9"/><line x1="9" y1="1" x2="1" y2="9"/>
          </svg>
        </button>
      `;
      slots.appendChild(slot);
    });

    // Empty slot indicators
    const remaining = MAX_PRODUCTS - n;
    for (let i = 0; i < remaining; i++) {
      const empty = document.createElement('div');
      empty.className = 'ic-cmp-bar__empty-slot';
      empty.setAttribute('aria-hidden', 'true');
      empty.innerHTML = `
        <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <rect x="3" y="3" width="18" height="18" rx="3" stroke-width="1.5" fill="none"/>
        </svg>
      `;
      slots.appendChild(empty);
    }
  }

  /* ─────────────────────────────────────────────────────────────
     Escape HTML
  ───────────────────────────────────────────────────────────── */
  function escHtml(str) {
    if (!str) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  /* ─────────────────────────────────────────────────────────────
     Open drawer
  ───────────────────────────────────────────────────────────── */
  function openDrawer() {
    const drawer  = $('#ic-cmp-drawer');
    const overlay = $('#ic-cmp-overlay');
    if (!drawer) return;

    // Login gate
    if (!cfg.customerLoggedIn) {
      renderLoginGate(drawer);
    } else {
      renderCompareTable(drawer);
    }

    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    if (overlay) {
      overlay.classList.add('is-visible');
      overlay.setAttribute('aria-hidden', 'false');
    }
    document.body.classList.add('ic-cmp-drawer-open');
    state.drawerOpen = true;

    // Focus management
    setTimeout(() => {
      const close = $('#ic-cmp-drawer-close');
      if (close) close.focus();
    }, 50);

    // Trap focus
    state.focusTrapCleanup = trapFocus(drawer);

    trackEvent('compare_view_opened', {
      product_count: state.products.length,
      product_ids:   state.products.map(p => p.id).join(','),
      logged_in:     cfg.customerLoggedIn
    });
  }

  /* ─────────────────────────────────────────────────────────────
     Close drawer
  ───────────────────────────────────────────────────────────── */
  function closeDrawer() {
    const drawer  = $('#ic-cmp-drawer');
    const overlay = $('#ic-cmp-overlay');
    if (!drawer) return;

    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    if (overlay) {
      overlay.classList.remove('is-visible');
      overlay.setAttribute('aria-hidden', 'true');
    }
    document.body.classList.remove('ic-cmp-drawer-open');
    state.drawerOpen = false;

    if (state.focusTrapCleanup) {
      state.focusTrapCleanup();
      state.focusTrapCleanup = null;
    }

    // Return focus to the "Compare now" button
    const openBtn = $('#ic-cmp-bar-open');
    if (openBtn) openBtn.focus();
  }

  /* ─────────────────────────────────────────────────────────────
     Focus trap
  ───────────────────────────────────────────────────────────── */
  function trapFocus(container) {
    const focusable = 'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])';

    function handleKeyDown(e) {
      if (e.key !== 'Tab') return;
      const els = Array.from(container.querySelectorAll(focusable)).filter(el => !el.closest('[hidden]') && el.offsetParent !== null);
      if (!els.length) return;
      const first = els[0];
      const last  = els[els.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) { e.preventDefault(); last.focus(); }
      } else {
        if (document.activeElement === last)  { e.preventDefault(); first.focus(); }
      }
    }

    function handleEsc(e) {
      if (e.key === 'Escape') closeDrawer();
    }

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('keydown', handleEsc);

    return function cleanup() {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('keydown', handleEsc);
    };
  }

  /* ─────────────────────────────────────────────────────────────
     Login gate HTML
  ───────────────────────────────────────────────────────────── */
  function renderLoginGate(drawer) {
    const body = $('#ic-cmp-drawer-body', drawer);
    if (!body) return;

    body.innerHTML = `
      <div class="ic-cmp-login-gate">
        <div class="ic-cmp-login-gate__icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
            <circle cx="12" cy="7" r="4"/>
          </svg>
        </div>
        <h3 class="ic-cmp-login-gate__title">Sign in to Compare Products</h3>
        <p class="ic-cmp-login-gate__body">
          Create a free account or sign in to use our product compare tool and find exactly what you're looking for.
        </p>
        <div class="ic-cmp-login-gate__perks" aria-label="Account benefits">
          ${['Side-by-side product comparison', 'Save wishlists and favourites', 'Track your orders easily', 'Exclusive member offers'].map(perk => `
            <div class="ic-cmp-login-gate__perk">
              <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
              <span>${perk}</span>
            </div>
          `).join('')}
        </div>
        <div class="ic-cmp-login-gate__actions">
          <a
            href="${escHtml(cfg.loginUrl)}?return_url=${encodeURIComponent(getShareableUrl())}"
            class="ic-cmp-login-gate__btn ic-cmp-login-gate__btn--primary"
          >Sign in</a>
          <a
            href="${escHtml(cfg.registerUrl)}?return_url=${encodeURIComponent(getShareableUrl())}"
            class="ic-cmp-login-gate__btn ic-cmp-login-gate__btn--outline"
          >Create account</a>
        </div>
      </div>
    `;
  }

  /* ─────────────────────────────────────────────────────────────
     Stars HTML
  ───────────────────────────────────────────────────────────── */
  function starsHtml(rating, max, count) {
    if (!rating) return '<span style="color:var(--ic-cmp-text-light);font-size:0.78rem">No reviews yet</span>';
    const stars = Math.round(rating * 2) / 2; // nearest 0.5
    const full  = Math.floor(stars);
    const half  = stars % 1 !== 0 ? 1 : 0;
    const empty = Math.ceil(max) - full - half;

    const starSvg = (type) => {
      if (type === 'full') return `<svg class="ic-cmp-stars__icon" viewBox="0 0 24 24"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>`;
      if (type === 'half') return `<svg class="ic-cmp-stars__icon" viewBox="0 0 24 24" style="opacity:0.55"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>`;
      return `<svg class="ic-cmp-stars__icon ic-cmp-stars__icon--empty" viewBox="0 0 24 24"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>`;
    };

    const icons = Array(full).fill(starSvg('full')).concat(Array(half).fill(starSvg('half'))).concat(Array(empty).fill(starSvg('empty'))).join('');
    const countLabel = count ? `(${count.toLocaleString()})` : '';

    return `
      <div class="ic-cmp-stars" aria-label="${rating} out of ${max} stars${count ? ', ' + count + ' reviews' : ''}">
        <div class="ic-cmp-stars__icons" aria-hidden="true">${icons}</div>
        ${countLabel ? `<span class="ic-cmp-stars__count">${escHtml(countLabel)}</span>` : ''}
      </div>
    `;
  }

  /* ─────────────────────────────────────────────────────────────
     Availability badge HTML
  ───────────────────────────────────────────────────────────── */
  function availabilityHtml(product) {
    if (product.available) {
      return `<span class="ic-cmp-badge ic-cmp-badge--in-stock"><span class="ic-cmp-badge__dot" aria-hidden="true"></span>In stock</span>`;
    }
    return `<span class="ic-cmp-badge ic-cmp-badge--oos"><span class="ic-cmp-badge__dot" aria-hidden="true"></span>Out of stock</span>`;
  }

  /* ─────────────────────────────────────────────────────────────
     Price HTML
  ───────────────────────────────────────────────────────────── */
  function priceHtml(product) {
    let html = `<span class="ic-cmp-table__price-current">${escHtml(product.price)}</span>`;
    if (product.comparePrice) {
      html += ` <span class="ic-cmp-table__price-compare">${escHtml(product.comparePrice)}</span>`;
      // Calculate savings %
      try {
        const curr = parseFloat((product.price || '').replace(/[^0-9.]/g, ''));
        const comp = parseFloat((product.comparePrice || '').replace(/[^0-9.]/g, ''));
        if (comp > curr) {
          const pct = Math.round((1 - curr / comp) * 100);
          html += ` <span class="ic-cmp-table__price-badge">-${pct}%</span>`;
        }
      } catch (e) { /* ignore parse errors */ }
    }
    return `<div class="ic-cmp-table__product-price">${html}</div>`;
  }

  /* ─────────────────────────────────────────────────────────────
     Add to cart from compare view
  ───────────────────────────────────────────────────────────── */
  async function addToCartFromCompare(btn) {
    const variantId = btn.dataset.variantId;
    const productId = btn.dataset.productId;
    if (!variantId) return;

    btn.disabled = true;
    const originalText = btn.textContent;
    btn.textContent = 'Adding...';

    try {
      const response = await fetch(cfg.cartAddUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 1 })
      });

      if (!response.ok) throw new Error('Cart add failed');

      btn.textContent = 'Added!';
      btn.style.background = 'var(--ic-cmp-success)';

      // Fire GA4 event
      const product = state.products.find(p => p.id === productId);
      if (product) {
        trackEvent('compare_add_to_cart', {
          product_id:    product.id,
          product_title: product.title,
          product_handle: product.handle,
          variant_id:    variantId,
          value:         product.price
        });
      }

      // Trigger Dawn's cart update event
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      // Also trigger Shopify cart drawer if present
      document.dispatchEvent(new CustomEvent('cart-drawer:open', { bubbles: true }));

      setTimeout(() => {
        btn.textContent = originalText;
        btn.style.background = '';
        btn.disabled = false;
      }, 2000);

    } catch (e) {
      btn.textContent = 'Error — retry';
      btn.style.background = 'var(--ic-cmp-danger)';
      btn.disabled = false;
      setTimeout(() => {
        btn.textContent = originalText;
        btn.style.background = '';
      }, 2500);
    }
  }

  /* ─────────────────────────────────────────────────────────────
     Remove product from inside the drawer
  ───────────────────────────────────────────────────────────── */
  function removeProductFromDrawer(productId) {
    const idx = state.products.findIndex(p => p.id === productId);
    if (idx === -1) return;

    const removed = state.products.splice(idx, 1)[0];
    saveToStorage(state.products);
    syncAllToggles();
    updateBar();

    trackEvent('compare_remove', {
      product_id:    removed.id,
      product_title: removed.title,
      product_handle: removed.handle,
      source:        'drawer'
    });

    if (state.products.length < MIN_TO_COMPARE) {
      closeDrawer();
      showToast('Add at least 2 products to compare.');
    } else {
      // Re-render drawer
      const drawer = $('#ic-cmp-drawer');
      if (drawer) renderCompareTable(drawer);
    }
  }

  /* ─────────────────────────────────────────────────────────────
     Build compare table HTML
  ───────────────────────────────────────────────────────────── */
  function renderCompareTable(drawer) {
    const body = $('#ic-cmp-drawer-body', drawer);
    if (!body) return;

    const products = state.products;
    const n = products.length;

    // Update CSS variable for grid columns
    const tableWrap = body.querySelector('.ic-cmp-table-wrap');

    /* ── product header cards ── */
    const productCardCols = products.map(product => `
      <div class="ic-cmp-table__product-card">
        <button
          type="button"
          class="ic-cmp-table__product-remove"
          aria-label="Remove ${escHtml(product.title)} from compare"
          data-drawer-remove-id="${escHtml(product.id)}"
        >
          <svg viewBox="0 0 10 10" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <line x1="1" y1="1" x2="9" y2="9"/><line x1="9" y1="1" x2="1" y2="9"/>
          </svg>
        </button>

        <div class="ic-cmp-table__product-img-wrap">
          ${product.image
            ? `<img src="${escHtml(product.image)}" alt="${escHtml(product.title)}" class="ic-cmp-table__product-img" loading="lazy" width="300" height="300">`
            : `<div class="ic-cmp-table__product-img" style="display:flex;align-items:center;justify-content:center;color:var(--ic-cmp-text-light);font-size:0.78rem;">No image</div>`
          }
        </div>

        <a href="${escHtml(product.url)}" class="ic-cmp-table__product-title">${escHtml(product.title)}</a>
        ${priceHtml(product)}

        <div class="ic-cmp-table__product-actions">
          ${product.available
            ? `<button
                 type="button"
                 class="ic-cmp-table__btn ic-cmp-table__btn--atc"
                 data-ic-cmp-atc
                 data-variant-id="${escHtml(product.variantId)}"
                 data-product-id="${escHtml(product.id)}"
                 aria-label="Add ${escHtml(product.title)} to cart"
               >Add to cart</button>`
            : `<button type="button" class="ic-cmp-table__btn ic-cmp-table__btn--atc" disabled aria-disabled="true">Out of stock</button>`
          }
          <a
            href="${escHtml(product.url)}"
            class="ic-cmp-table__btn ic-cmp-table__btn--view"
            aria-label="View ${escHtml(product.title)}"
          >View product</a>
        </div>
      </div>
    `).join('');

    /* ── Data rows definition ── */
    const rows = [
      {
        label: 'Availability',
        key: 'availability',
        values: products.map(p => availabilityHtml(p)),
        raw:    products.map(p => p.available ? 'in-stock' : 'out-of-stock')
      },
      {
        label: 'Rating',
        key: 'rating',
        values: products.map(p => starsHtml(p.rating, p.ratingMax, p.ratingCount)),
        raw:    products.map(p => String(p.rating || ''))
      },
      {
        label: 'Brand / Franchise',
        key: 'brand',
        values: products.map(p => escHtml(getBrandFromTags(p.tags) || p.vendor) || '<span class="ic-cmp-row__cell--empty">—</span>'),
        raw:    products.map(p => getBrandFromTags(p.tags) || p.vendor || '')
      },
      {
        label: 'Material / Finish',
        key: 'material',
        values: products.map(p => escHtml(p.metafields.material) || '<span class="ic-cmp-row__cell--empty">—</span>'),
        raw:    products.map(p => p.metafields.material || '')
      },
      {
        label: 'Size / Dimensions',
        key: 'dimensions',
        values: products.map(p => escHtml(p.metafields.dimensions) || '<span class="ic-cmp-row__cell--empty">—</span>'),
        raw:    products.map(p => p.metafields.dimensions || '')
      },
      {
        label: 'Pack / Set Size',
        key: 'packSize',
        values: products.map(p => escHtml(p.metafields.packSize) || '<span class="ic-cmp-row__cell--empty">—</span>'),
        raw:    products.map(p => p.metafields.packSize || '')
      },
      {
        label: 'Officially Licensed',
        key: 'licensed',
        values: products.map(p => {
          const val = p.metafields.licensed;
          if (!val) return '<span class="ic-cmp-row__cell--empty">—</span>';
          const isYes = /^(true|yes|1)$/i.test(val);
          return isYes
            ? '<span class="ic-cmp-badge ic-cmp-badge--in-stock">Yes</span>'
            : `<span style="color:var(--ic-cmp-text-muted)">${escHtml(val)}</span>`;
        }),
        raw: products.map(p => p.metafields.licensed || '')
      },
      {
        label: 'Age Rating',
        key: 'ageRating',
        values: products.map(p => escHtml(p.metafields.ageRating) || '<span class="ic-cmp-row__cell--empty">—</span>'),
        raw:    products.map(p => p.metafields.ageRating || '')
      },
      {
        label: 'No. of Players',
        key: 'players',
        values: products.map(p => escHtml(p.metafields.players) || '<span class="ic-cmp-row__cell--empty">—</span>'),
        raw:    products.map(p => p.metafields.players || '')
      },
      {
        label: 'Play Time',
        key: 'playTime',
        values: products.map(p => escHtml(p.metafields.playTime) || '<span class="ic-cmp-row__cell--empty">—</span>'),
        raw:    products.map(p => p.metafields.playTime || '')
      },
      {
        label: 'Product Type',
        key: 'type',
        values: products.map(p => escHtml(p.type) || '<span class="ic-cmp-row__cell--empty">—</span>'),
        raw:    products.map(p => p.type || '')
      }
    ];

    /* ── Determine which rows have differences ── */
    rows.forEach(row => {
      const unique = new Set(row.raw.map(v => v.trim().toLowerCase()));
      row.isDiff = unique.size > 1;
      row.allEmpty = row.raw.every(v => !v.trim());
    });

    /* ── Build row HTML ── */
    const rowsHtml = rows.map(row => {
      if (row.allEmpty) return ''; // hide entirely if no products have this field
      const cellsHtml = row.values.map(v => `<div class="ic-cmp-row__cell">${v}</div>`).join('');
      const diffClass = row.isDiff ? 'ic-cmp-row--diff' : '';
      return `
        <div class="ic-cmp-row ${diffClass}" data-row-key="${row.key}" data-is-diff="${row.isDiff}">
          <div class="ic-cmp-row__label ic-cmp-table__label-col">${escHtml(row.label)}</div>
          ${cellsHtml}
        </div>
      `;
    }).join('');

    /* ── Share URL row ── */
    const shareUrl = getShareableUrl();

    /* ── Assemble ── */
    body.innerHTML = `
      <div class="ic-cmp-table-wrap">
        <div class="ic-cmp-table" style="--ic-cmp-cols:${n};" role="table" aria-label="Product comparison table">

          <div class="ic-cmp-table__head" role="rowgroup">
            <div class="ic-cmp-table__head-label ic-cmp-table__label-col" role="columnheader">
              <span class="visually-hidden">Attribute</span>
            </div>
            ${productCardCols}
          </div>

          ${rowsHtml}

        </div>

        <div style="padding:1rem 1.25rem;border-top:1px solid var(--ic-cmp-border);display:flex;align-items:center;gap:0.75rem;flex-wrap:wrap;">
          <span style="font-size:0.78rem;color:var(--ic-cmp-text-muted);">Share comparison:</span>
          <button
            type="button"
            id="ic-cmp-share-btn"
            style="height:32px;padding:0 1rem;border-radius:16px;border:1.5px solid var(--ic-cmp-border);background:transparent;font-size:0.78rem;font-weight:600;cursor:pointer;color:var(--ic-cmp-text-muted);transition:border-color 200ms,color 200ms;"
            data-share-url="${escHtml(shareUrl)}"
            aria-label="Copy shareable comparison link"
          >Copy link</button>
        </div>
      </div>
    `;

    // Wire up "highlight differences" toggle
    appleDiffFilter($('#ic-cmp-diff-toggle'));
  }

  /* ─────────────────────────────────────────────────────────────
     Highlight differences toggle
  ───────────────────────────────────────────────────────────── */
  function appleDiffFilter(checkbox) {
    if (!checkbox) return;

    function applyDiff() {
      const showDiffOnly = checkbox.checked;
      const drawer = $('#ic-cmp-drawer');
      if (!drawer) return;

      $$('[data-row-key]', drawer).forEach(row => {
        const isDiff   = row.dataset.isDiff === 'true';
        const allEmpty = !isDiff && row.querySelectorAll('.ic-cmp-row__cell--empty').length === row.querySelectorAll('.ic-cmp-row__cell').length;

        if (showDiffOnly) {
          row.classList.toggle('ic-cmp-row--same-hidden', !isDiff);
        } else {
          row.classList.remove('ic-cmp-row--same-hidden');
        }
      });
    }

    checkbox.addEventListener('change', applyDiff);
  }

  /* ─────────────────────────────────────────────────────────────
     Share button — copy URL to clipboard
  ───────────────────────────────────────────────────────────── */
  function handleShareBtn(btn) {
    const url = btn.dataset.shareUrl;
    if (!url) return;
    if (navigator.clipboard) {
      navigator.clipboard.writeText(url).then(() => {
        btn.textContent = 'Copied!';
        setTimeout(() => { btn.textContent = 'Copy link'; }, 2000);
      });
    } else {
      // Fallback
      const ta = document.createElement('textarea');
      ta.value = url;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      btn.textContent = 'Copied!';
      setTimeout(() => { btn.textContent = 'Copy link'; }, 2000);
    }
  }

  /* ─────────────────────────────────────────────────────────────
     Clear all
  ───────────────────────────────────────────────────────────── */
  function clearAll() {
    state.products = [];
    saveToStorage([]);
    syncAllToggles();
    updateBar();
    if (state.drawerOpen) closeDrawer();
  }

  /* ─────────────────────────────────────────────────────────────
     Delegate click handler (handles dynamically added cards)
  ───────────────────────────────────────────────────────────── */
  function handleDocumentClick(e) {
    const target = e.target;

    // Compare toggle on product card
    const toggle = target.closest('[data-ic-cmp-toggle]');
    if (toggle) {
      e.preventDefault();
      toggleProduct(toggle);
      return;
    }

    // Remove from bar slot
    const barRemove = target.closest('[data-remove-id]');
    if (barRemove) {
      const id = barRemove.dataset.removeId;
      const idx = state.products.findIndex(p => p.id === id);
      if (idx > -1) {
        const removed = state.products.splice(idx, 1)[0];
        saveToStorage(state.products);
        syncAllToggles();
        updateBar();
        trackEvent('compare_remove', {
          product_id: removed.id,
          product_title: removed.title,
          source: 'bar'
        });
      }
      return;
    }

    // Remove from drawer table
    const drawerRemove = target.closest('[data-drawer-remove-id]');
    if (drawerRemove) {
      removeProductFromDrawer(drawerRemove.dataset.drawerRemoveId);
      return;
    }

    // Open compare drawer
    if (target.closest('#ic-cmp-bar-open')) {
      openDrawer();
      return;
    }

    // Close drawer
    if (target.closest('#ic-cmp-drawer-close') || target.closest('#ic-cmp-overlay')) {
      closeDrawer();
      return;
    }

    // Clear all
    if (target.closest('#ic-cmp-bar-clear')) {
      clearAll();
      return;
    }

    // Add to cart from compare
    const atcBtn = target.closest('[data-ic-cmp-atc]');
    if (atcBtn) {
      addToCartFromCompare(atcBtn);
      return;
    }

    // Share button
    const shareBtn = target.closest('#ic-cmp-share-btn');
    if (shareBtn) {
      handleShareBtn(shareBtn);
      return;
    }
  }

  /* ─────────────────────────────────────────────────────────────
     MutationObserver — sync new product cards added to DOM
     (infinite scroll, quick-add modals, etc.)
  ───────────────────────────────────────────────────────────── */
  function observeNewCards() {
    const observer = new MutationObserver(() => {
      syncAllToggles();
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  /* ─────────────────────────────────────────────────────────────
     Restore from URL params on page load
  ───────────────────────────────────────────────────────────── */
  function restoreFromUrl() {
    const handles = readUrlParams();
    if (!handles.length) return;

    // Cross-reference with toggles currently on the page
    handles.forEach(handle => {
      const btn = document.querySelector(`[data-ic-cmp-toggle][data-product-handle="${CSS.escape(handle)}"]`);
      if (btn && !state.products.find(p => p.handle === handle)) {
        if (state.products.length < MAX_PRODUCTS) {
          state.products.push(extractProductData(btn));
        }
      }
    });

    if (state.products.length >= MIN_TO_COMPARE) {
      saveToStorage(state.products);
      syncAllToggles();
      updateBar();
      // Auto-open the drawer if URL had compare params
      setTimeout(openDrawer, 600);
    }
  }

  /* ─────────────────────────────────────────────────────────────
     Init
  ───────────────────────────────────────────────────────────── */
  function init() {
    // Restore session
    const stored = loadFromStorage();
    if (stored.length) {
      state.products = stored.slice(0, MAX_PRODUCTS);
    }

    // Event delegation — click and keyboard (div role=button needs keydown)
    document.addEventListener('click', handleDocumentClick, true);
    document.addEventListener('keydown', function(e) {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      const toggle = e.target.closest('[data-ic-cmp-toggle]');
      if (toggle) { e.preventDefault(); toggleProduct(toggle); }
    }, true);

    // Sync toggles on DOM ready
    syncAllToggles();
    updateBar();

    // Watch for new cards (infinite scroll etc.)
    observeNewCards();

    // Restore from URL
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', restoreFromUrl);
    } else {
      restoreFromUrl();
    }
  }

  /* ─────────────────────────────────────────────────────────────
     Boot
  ───────────────────────────────────────────────────────────── */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
